import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCollection } from '../hooks/useFirestore'
import DOMPurify from 'dompurify'
import './LoreArticlePage.css'

// Sanitize HTML content using DOMPurify to prevent XSS
function useSanitizedHtml(html) {
  return useMemo(() => {
    if (!html) return ''
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'hr', 'pre', 'code'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel']
    })
  }, [html])
}

export default function LoreArticlePage() {
  const { slug } = useParams()
  const { isOwner } = useAuth()
  const { data: articles, loading } = useCollection('lore')

  // Find article by slug or id
  const article = articles.find(a => a.slug === slug || a.id === slug)

  // Sanitize content with DOMPurify (XSS protection)
  const sanitizedContent = useSanitizedHtml(article?.content)

  if (loading) {
    return (
      <div className="container">
        <div className="article-loading">
          <div className="loading-spinner" />
          <p>Loading article...</p>
        </div>
      </div>
    )
  }

  if (!article) {
    return (
      <div className="container">
        <div className="article-not-found">
          <h1>Article Not Found</h1>
          <p>The lore article you're looking for doesn't exist.</p>
          <Link to="/lore" className="btn btn-primary">
            Back to Lore
          </Link>
        </div>
      </div>
    )
  }

  // Hide article from non-owners if hidden flag is set
  if (article.hidden === true && !isOwner) {
    return (
      <div className="container">
        <div className="article-not-found">
          <h1>Article Not Found</h1>
          <p>The lore article you're looking for doesn't exist.</p>
          <Link to="/lore" className="btn btn-primary">
            Back to Lore
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <article className="lore-article">
        <header className="article-header">
          <Link to="/lore" className="back-link">
            ← Back to Lore
          </Link>

          {article.category && (
            <span className="article-category">{article.category}</span>
          )}

          <h1>{article.title}</h1>

          {article.excerpt && (
            <p className="article-excerpt">{article.excerpt}</p>
          )}
        </header>

        {article.coverImageUrl && (
          <div className="article-cover">
            <img src={article.coverImageUrl} alt={article.title} />
          </div>
        )}

        <div className="article-content">
          <SanitizedHtml html={sanitizedContent} />
        </div>

        <footer className="article-footer">
          <Link to="/lore" className="btn btn-ghost">
            ← Back to Lore
          </Link>
        </footer>
      </article>
    </div>
  )
}

// Component that renders pre-sanitized HTML safely
function SanitizedHtml({ html }) {
  if (!html) return null
  // Content is already sanitized via DOMPurify in useSanitizedHtml hook
  return <div dangerouslySetInnerHTML={{ __html: html }} />
}
